import React, { useState, useEffect } from 'react';
import { updateTenantStatus, deleteUser, deleteTenant } from 'services/api/AccessManagerAPI';
import { UserRole } from 'types/userRoles';
import { useGetAccessManager, useGetTenant } from 'services/hooks/queries';
import {
    UsersSection,
    TenantsSection,
    NotificationModal,
    ConfirmationModal,
    EditUserModal,
    CreateTenantModal,
} from './components';
import { MappedUser, TenantUser, Tenant, NotificationState, ConfirmModalState } from './types';
import { Button } from 'common/components/Button';
import styles from './AccessManager.module.scss';

const AccessManager: React.FC = () => {
    // State for tenants and users
    const [tenants, setTenants] = useState<Tenant[]>([]);
    const [users, setUsers] = useState<MappedUser[]>([]);
    const [hasTenantAccess, setHasTenantAccess] = useState<boolean>(false);
    const [currentUserTenantId, setCurrentUserTenantId] = useState<string | null>(null);

    const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);

    // Modal states
    const [isCreateTenantModalOpen, setIsCreateTenantModalOpen] = useState<boolean>(false);
    const [isAddUserModalOpen, setIsAddUserModalOpen] = useState<boolean>(false);
    const [isInviteUserModalOpen, setIsInviteUserModalOpen] = useState<boolean>(false);
    const [isEditUserModalOpen, setIsEditUserModalOpen] = useState<boolean>(false);
    const [selectedUser, setSelectedUser] = useState<MappedUser | null>(null);

    // Modal states for notifications and confirmations
    const [confirmModal, setConfirmModal] = useState<ConfirmModalState>({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: () => {},
    });

    const [notificationModal, setNotificationModal] = useState<NotificationState>({
        isOpen: false,
        title: '',
        message: '',
        type: 'success',
    });

    const { accessManagerData, refetch: refetchAccessManager } = useGetAccessManager();

    const { tenantData, isTenantLoading } = useGetTenant(selectedTenant?.id);

    // Fetch and map data
    useEffect(() => {
        if (!accessManagerData) return;

        if (accessManagerData.tenants?.length) {
            setTenants(accessManagerData.tenants);
            setHasTenantAccess(true);
        } else {
            setHasTenantAccess(false);
        }

        const mappedUsers: MappedUser[] =
            accessManagerData.users?.map((user: any) => ({
                id: user.id,
                firstName: user.first_name,
                lastName: user.last_name,
                email: user.email,
                tenantId: user.tenant_id,
                roles: user.roles,
                createdAt: user.created_at,
                updatedAt: user.updated_at,
                cognitoId: user.cognito_id,
                agentIsOnline: user.agent_is_online,
                edges: user.edges,
                inviteStatus: user.invite_status,
            })) || [];

        setUsers(mappedUsers);

        // If we don't have tenant access, extract current user's tenantId
        if (!accessManagerData.tenants && mappedUsers?.length > 0) {
            const currentUser = mappedUsers.find(
                (user) => user.roles.includes(UserRole.SUPER_ADMIN) || user.roles.includes(UserRole.ADMIN)
            );
            if (currentUser && currentUser.tenantId) {
                setCurrentUserTenantId(currentUser.tenantId);
            }
        }
    }, [accessManagerData]);

    useEffect(() => {
        if (tenantData) {
            const mappedUsers: MappedUser[] = tenantData.map((user: any) => ({
                id: user.id,
                firstName: user.first_name,
                lastName: user.last_name,
                email: user.email,
                tenantId: user.tenant_id,
                roles: user.roles,
                createdAt: user.created_at,
                updatedAt: user.updated_at,
                inviteStatus: user.invite_status,
            }));
            setUsers(mappedUsers);
        }
    }, [tenantData]);

    // Handle tenant selection
    const handleTenantSelect = (tenant: Tenant) => {
        setSelectedTenant(tenant);
    };

    // Handle tenant deactivation
    const handleTenantDeactivation = async (tenant: Tenant): Promise<void> => {
        try {
            const isCurrentlyActive = tenant.config?.status === 'ACTIVE';
            await updateTenantStatus(tenant.id);

            const updatedTenants = tenants.map((t) => {
                if (t.id === tenant.id) {
                    return {
                        ...t,
                        config: {
                            ...t.config,
                            status: t.config?.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE',
                        },
                    };
                }
                return t;
            });

            setTenants(updatedTenants);

            setNotificationModal({
                isOpen: true,
                title: 'Success',
                message: `Tenant ${isCurrentlyActive ? 'deactivated' : 'activated'} successfully!`,
                type: 'success',
            });
        } catch (error) {
            console.error('Error updating tenant status:', error);
            setNotificationModal({
                isOpen: true,
                title: 'Error',
                message: 'Failed to update tenant status. Please try again.',
                type: 'error',
            });
        }
    };

    // Handle successful user addition
    const handleAddUserSuccess = async (createdUser: MappedUser, wasAddedToDifferentTenant: boolean): Promise<void> => {
        if (wasAddedToDifferentTenant) {
            // Refetch all data - useEffect will handle mapping users and tenants
            const { data } = await refetchAccessManager();

            // If user was added to a tenant, select it and fetch its users
            if (createdUser.tenantId && data?.tenants) {
                const tenant = data.tenants.find((t: any) => t.id === createdUser.tenantId);
                if (tenant) {
                    setSelectedTenant(tenant);
                    await handleTenantSelect(tenant);
                }
            }
        } else {
            // Optimistically update local state
            setUsers((prevUsers) => [...prevUsers, createdUser]);
        }

        setNotificationModal({
            isOpen: true,
            title: 'Success',
            message: 'User created successfully!',
            type: 'success',
        });
    };

    // Handle user addition error
    const handleAddUserError = (message: string): void => {
        setNotificationModal({
            isOpen: true,
            title: 'Error',
            message,
            type: 'error',
        });
    };

    const handleInviteUserSuccess = async (
        createdUser: MappedUser,
        wasAddedToDifferentTenant: boolean
    ): Promise<void> => {
        if (wasAddedToDifferentTenant) {
            const { data } = await refetchAccessManager();

            if (createdUser.tenantId && data?.tenants) {
                const tenant = data.tenants.find((t: any) => t.id === createdUser.tenantId);
                if (tenant) {
                    setSelectedTenant(tenant);
                    await handleTenantSelect(tenant);
                }
            }
        } else {
            setUsers((prevUsers) => [...prevUsers, createdUser]);
        }

        setNotificationModal({
            isOpen: true,
            title: 'Success',
            message: 'Invitation email sent. The user will set their own password.',
            type: 'success',
        });
    };

    const handleInviteUserError = (message: string): void => {
        setNotificationModal({
            isOpen: true,
            title: 'Error',
            message,
            type: 'error',
        });
    };

    // Handle edit user
    const handleEditUser = (user: MappedUser | TenantUser): void => {
        setSelectedUser(user as MappedUser);
        setIsEditUserModalOpen(true);
    };

    // Handle successful user update
    const handleEditUserSuccess = (updatedUser: MappedUser): void => {
        // Optimistically update local state
        setUsers((prevUsers) => prevUsers.map((user) => (user.id === updatedUser.id ? updatedUser : user)));
        setSelectedUser(null);

        setNotificationModal({
            isOpen: true,
            title: 'Success',
            message: 'User updated successfully!',
            type: 'success',
        });
    };

    // Handle user update error
    const handleEditUserError = (message: string): void => {
        setNotificationModal({
            isOpen: true,
            title: 'Error',
            message,
            type: 'error',
        });
    };

    // Handle successful tenant creation
    const handleCreateTenantSuccess = (tenant: Tenant): void => {
        // Optimistically update local state
        setTenants((prevTenants) => [...prevTenants, tenant]);

        setNotificationModal({
            isOpen: true,
            title: 'Success',
            message: 'Tenant created successfully!',
            type: 'success',
        });
    };

    // Handle tenant creation error
    const handleCreateTenantError = (message: string): void => {
        setNotificationModal({
            isOpen: true,
            title: 'Error',
            message,
            type: 'error',
        });
    };

    // Handle delete user
    const handleDeleteUser = (user: MappedUser | TenantUser): void => {
        setConfirmModal({
            isOpen: true,
            title: 'Confirm Deletion',
            message: `Are you sure you want to delete ${user.firstName} ${user.lastName}?`,
            onConfirm: async () => {
                try {
                    await deleteUser(user.id);

                    // Optimistically update local state
                    setUsers((prevUsers) => prevUsers.filter((u) => u.id !== user.id));

                    setNotificationModal({
                        isOpen: true,
                        title: 'Success',
                        message: 'User deleted successfully!',
                        type: 'success',
                    });
                } catch (error) {
                    console.error('Error deleting user:', error);
                    setNotificationModal({
                        isOpen: true,
                        title: 'Error',
                        message: 'Failed to delete user. Please try again.',
                        type: 'error',
                    });
                }
            },
        });
    };

    // Handle tenant deletion
    const handleDeleteTenant = (tenant: Tenant, e: React.MouseEvent): void => {
        e.stopPropagation();

        setConfirmModal({
            isOpen: true,
            title: 'Confirm Deletion',
            message: `Are you sure you want to delete the tenant "${tenant.name}"? This action cannot be undone.`,
            onConfirm: async () => {
                try {
                    await deleteTenant(tenant.id);

                    // Optimistically update local state
                    setTenants((prevTenants) => prevTenants.filter((t) => t.id !== tenant.id));

                    if (selectedTenant && selectedTenant.id === tenant.id) {
                        setSelectedTenant(null);
                        setUsers([]);
                    }

                    setNotificationModal({
                        isOpen: true,
                        title: 'Success',
                        message: 'Tenant deleted successfully!',
                        type: 'success',
                    });
                } catch (error) {
                    console.error('Error deleting tenant:', error);
                    setNotificationModal({
                        isOpen: true,
                        title: 'Error',
                        message: 'Failed to delete tenant. Please try again.',
                        type: 'error',
                    });
                }
            },
        });
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1>Access Manager</h1>
                {hasTenantAccess && (
                    <Button variant='primary' onClick={() => setIsCreateTenantModalOpen(true)}>
                        Create New Tenant
                    </Button>
                )}
            </div>

            <div className={`${styles.content} ${hasTenantAccess ? styles.twoColumns : styles.oneColumn}`}>
                {hasTenantAccess && (
                    <TenantsSection
                        tenants={tenants}
                        selectedTenant={selectedTenant}
                        onTenantSelect={handleTenantSelect}
                        onTenantToggleStatus={handleTenantDeactivation}
                        onTenantDelete={handleDeleteTenant}
                    />
                )}

                <div className={styles.section}>
                    <UsersSection
                        users={users}
                        isTenantLoading={isTenantLoading}
                        onEditUser={handleEditUser}
                        onDeleteUser={handleDeleteUser}
                        isAddUserModalOpen={isAddUserModalOpen}
                        onOpenAddUserModal={() => setIsAddUserModalOpen(true)}
                        onCloseAddUserModal={() => setIsAddUserModalOpen(false)}
                        onAddUserSuccess={handleAddUserSuccess}
                        onAddUserError={handleAddUserError}
                        isInviteUserModalOpen={isInviteUserModalOpen}
                        onOpenInviteUserModal={() => setIsInviteUserModalOpen(true)}
                        onCloseInviteUserModal={() => setIsInviteUserModalOpen(false)}
                        onInviteUserSuccess={handleInviteUserSuccess}
                        onInviteUserError={handleInviteUserError}
                        hasTenantAccess={hasTenantAccess}
                        tenants={tenants}
                        selectedTenant={selectedTenant}
                        selectedUser={selectedUser}
                        currentUserTenantId={currentUserTenantId}
                    />
                </div>
            </div>

            <CreateTenantModal
                isOpen={isCreateTenantModalOpen}
                onClose={() => setIsCreateTenantModalOpen(false)}
                onSuccess={handleCreateTenantSuccess}
                onError={handleCreateTenantError}
            />

            <EditUserModal
                isOpen={isEditUserModalOpen}
                onClose={() => {
                    setIsEditUserModalOpen(false);
                    setSelectedUser(null);
                }}
                user={selectedUser as any}
                onSuccess={handleEditUserSuccess as any}
                onError={handleEditUserError}
                hasTenantAccess={hasTenantAccess}
                selectedTenant={selectedTenant as any}
            />

            <ConfirmationModal
                isOpen={confirmModal.isOpen}
                title={confirmModal.title}
                message={confirmModal.message}
                onConfirm={confirmModal.onConfirm}
                onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
            />

            <NotificationModal
                isOpen={notificationModal.isOpen}
                type={notificationModal.type}
                title={notificationModal.title}
                message={notificationModal.message}
                onClose={() => setNotificationModal({ ...notificationModal, isOpen: false })}
            />
        </div>
    );
};

export default AccessManager;
